import { useState } from "react";
import { KeyRound, RefreshCw, Plus, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Licensing() {
  const { state, run, refresh } = useAzure();
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [assignUser, setAssignUser] = useState("");

  const skus = state?.license_skus || [];
  const users = state?.entra_users || [];
  const skuById = (id) => skus.find((s) => s.sku_id === id);

  const usersWithSku = (skuId) => users.filter((u) => (u.licenses || []).includes(skuId));

  const assign = () => {
    if (!assignUser || !selected) return;
    run(() => api.post("/azure/identity/licenses/assign", { user_id: assignUser, sku_id: selected.sku_id }),
      { success: "License assigned" }).then((r) => { if (r.ok) { setAssignUser(""); setOpen(false); } });
  };

  const remove = (userId) => {
    run(() => api.post("/azure/identity/licenses/remove", { user_id: userId, sku_id: selected.sku_id }),
      { success: "License removed" });
  };

  const detailUsers = selected ? usersWithSku(selected.sku_id) : [];
  const nonAssigned = selected ? users.filter((u) => !(u.licenses || []).includes(selected.sku_id)) : [];

  return (
    <div data-testid="az-licensing">
      <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
        <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
        <span className="text-xs text-muted-foreground ml-auto">Licenses are consumed per user. Set a usage location before assigning.</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {skus.map((s) => {
          const pct = s.total ? Math.round((s.consumed / s.total) * 100) : 0;
          return (
            <button key={s.sku_id} data-testid={`az-sku-${s.part_number}`} onClick={() => { setSelected(s); }}
              className="text-left border border-border rounded-lg bg-card p-4 hover:border-[#0078d4] transition-all">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="h-4 w-4 text-[#0078d4]" />
                <div className="font-semibold text-sm">{s.name}</div>
              </div>
              <div className="text-[11px] text-muted-foreground font-mono mb-3">{s.part_number}</div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-[#0078d4]" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-xs mt-1.5">
                <span className="text-muted-foreground">{s.consumed} assigned</span>
                <span className="font-medium text-emerald-600">{s.available} available</span>
              </div>
            </button>
          );
        })}
      </div>

      {selected && (
        <div className="fixed inset-y-0 right-0 w-full max-w-md bg-card border-l border-border shadow-2xl z-50 flex flex-col" data-testid="az-license-detail">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div><div className="font-display font-bold text-lg">{selected.name}</div><div className="text-xs text-muted-foreground font-mono">{selected.part_number}</div></div>
            <Button size="icon" variant="ghost" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <Button size="sm" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe] text-white" data-testid="az-license-assign-open" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Assign to user</Button>
            <span className="text-xs text-muted-foreground ml-auto">{selected.consumed}/{selected.total} used</span>
          </div>
          <div className="p-5 overflow-y-auto">
            <div className="text-sm font-semibold mb-2">Assigned users ({detailUsers.length})</div>
            <div className="space-y-1">
              {detailUsers.map((u) => (
                <div key={u.object_id} className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm">
                  <span>{u.display_name} <span className="text-muted-foreground text-xs">· {u.upn}</span></span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" data-testid={`az-license-remove-${u.mail_nickname}`} onClick={() => remove(u.object_id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              {detailUsers.length === 0 && <div className="text-muted-foreground text-sm">No users assigned yet.</div>}
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Assign {selected?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>User</Label>
              <Select value={assignUser} onValueChange={setAssignUser}>
                <SelectTrigger data-testid="az-license-user-select"><SelectValue placeholder="Select a user" /></SelectTrigger>
                <SelectContent>
                  {nonAssigned.map((u) => (
                    <SelectItem key={u.object_id} value={u.object_id}>
                      {u.display_name} {(u.usage_location || "").trim() ? "" : "(no usage location)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">A usage location is required. Set it on the user in the Users blade.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="az-license-assign-submit" onClick={assign} disabled={!assignUser} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
